import { countBy, identity, uniq } from 'es-toolkit';

import { max } from '@paragon/utilities';

import { HatchingMode } from './generateEnvelopeHatchLines';

/**
 * The automatic hatching mode -- returns a map from elevation to hatching mode for a set of bearing envelopes.
 *
 * The most common elevation (if there is a single one) is excluded from hatching. Remaining elevations are assigned
 *   hatching modes in ascending order, with each successive elevation getting one more stripe than the previous.
 */
export function createAutomaticElevationHatchingMap(elevations: readonly number[]): Map<number, HatchingMode> {
  const result = new Map<number, HatchingMode>();

  // if there are multiple "most common elevations" hatch them all
  const elevationCounts = countBy(elevations, identity);
  const maxElevationCount = max(Object.values(elevationCounts)) ?? 0;
  const elevationsWithMaxCount = Object.entries(elevationCounts)
    .filter(([, count]) => count === maxElevationCount)
    .map(([elevation]) => Number(elevation));
  const mostCommonElevation = elevationsWithMaxCount.length === 1 ? elevationsWithMaxCount[0] : null;

  // don't hatch the single most common elevation if there is one
  const uniqueElevations = uniq(elevations).toSorted((a, b) => a - b);
  const elevationsToHatch =
    mostCommonElevation != null ? uniqueElevations.filter((e) => e !== mostCommonElevation) : uniqueElevations;

  for (const [index, elevation] of elevationsToHatch.entries()) {
    result.set(elevation, index + 1);
  }

  return result;
}
